import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import {
  Box,
  TextField,
  Button,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Chip,
  Divider
} from "@mui/material";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const SoilTestTable = () => {
  const [data, setData] = useState([]);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [userId, setUserId] = useState("");
  const [status, setStatus] = useState("complete");
  const [loading, setLoading] = useState(false);

  // dialogs
  const [openShcDialog, setOpenShcDialog] = useState(false);
  const [openReportDialog, setOpenReportDialog] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const [token, setToken] = useState("");
  const [pdfLoading, setPdfLoading] = useState(false);

  // image preload for SHC (sample image)
  const [imageStatus, setImageStatus] = useState("idle"); // idle | loading | ok | fail
  const [imageSrc, setImageSrc] = useState("");

  const formatToIST = (utcString) => {
    if (!utcString) return "-";
    const date = new Date(utcString);
    const istDate = new Date(date.getTime() + 5.5 * 60 * 60 * 1000);
    return istDate.toLocaleString("en-IN", {
      dateStyle: "medium",
      timeStyle: "short",
      hour12: true
    });
  };

  // ====== SHC status helpers ======
  const getStatusBand = (value, bands) => {
    if (value === null || value === undefined || value === "") return { label: "NA" };
    var v = Number(value);
    if (Number.isNaN(v)) return { label: "NA" };

    for (var i = 0; i < bands.length; i++) {
      var b = bands[i];
      var minOk = b.min === null || v >= b.min;
      var maxOk = b.max === null || v <= b.max;
      if (minOk && maxOk) return { label: b.label };
    }
    return { label: "NA" };
  };

  const N_BANDS = [
    { label: "Low", min: null, max: 50 },
    { label: "Medium", min: 51, max: 100 },
    { label: "High", min: 101, max: null }
  ];
  const P_BANDS = [
    { label: "Low", min: null, max: 12 },
    { label: "Medium", min: 13, max: 25 },
    { label: "High", min: 26, max: null }
  ];
  const K_BANDS = [
    { label: "Low", min: null, max: 120 },
    { label: "Medium", min: 121, max: 280 },
    { label: "High", min: 281, max: null }
  ];
  const PH_BANDS = [
    { label: "Acidic", min: null, max: 5.5 },
    { label: "Slightly Acidic", min: 5.6, max: 6.5 },
    { label: "Neutral", min: 6.6, max: 7.5 },
    { label: "Alkaline", min: 7.6, max: null }
  ];

  const chipColorFor = (label) => {
    if (label === "High" || label === "Neutral") return "success";
    if (label === "Medium" || label === "Slightly Acidic") return "warning";
    if (label === "Low" || label === "Acidic" || label === "Alkaline") return "error";
    return "default";
  };

  // ====== Old Report labels + lab blanks ======
  const FIELD_LABELS = {
    id: "Test ID",
    user_id: "user id",
    test_start_time: "Test Start Time",
    soil_type: "Soil Type",
    depth_cm: "Depth (inch)",
    latitude: "Latitude",
    longitude: "Longitude",
    image_url: "Image URL",
    probe_placed: "Probe Placed",
    placement_validated: "Placement Validated",
    test_confirmed: "Test Confirmed",
    timer_started_at: "Timer Started At",
    timer_completed_at: "Timer Completed At",
    n: "Nitrogen (mg/kg)",
    p: "Phosphorus (mg/kg)",
    k: "Potassium (mg/kg)",
    moisture_percent: "Moisture (%RH)",
    temperature_c: "Temperature (°C)",
    electrical_conductivity: "Conductivity (µs/cm)",
    ph: "pH",
    remarks: "Remarks",
    test_status: "Test Status"
  };

  const LAB_FIELDS = [
    "Nitrogen (Lab Value)",
    "Phosphorus (Lab Value)",
    "Potassium (Lab Value)",
    "Moisture (Lab Value)",
    "Temperature (Lab Value)",
    "Conductivity (Lab Value)",
    "pH (Lab Value)",
    "Lab Analyst Name",
    "Signature",
    "Remarks (Lab)",
    "Other Soil Nutrients"
  ];

  // Keep report static ordering (like your original pdf)
  const REPORT_SECTIONS = useMemo(
    () => [
      {
        fields: [
          "id",
          "user_id",
          "test_start_time",
          "soil_type",
          "depth_cm",
          "latitude",
          "longitude",
          "image_url"
        ]
      },
      {
        fields: [
          "probe_placed",
          "placement_validated",
          "test_confirmed",
          "timer_started_at",
          "timer_completed_at",
          "n",
          "p",
          "k",
          "moisture_percent",
          "temperature_c",
          "electrical_conductivity",
          "ph",
          "remarks",
          "test_status"
        ]
      }
    ],
    []
  );

  // ====== Data fetch ======
  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      if (userId) params.user_id = userId;
      if (status) params.status = status;

      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/v1/soil_test`,
        { params }
      );

      const formattedData = response.data.map((item) => ({
        ...item,
        id: item.id
      }));
      setData(formattedData);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleFilter = () => fetchData();

  const handleReset = () => {
    setFromDate("");
    setToDate("");
    setUserId("");
    setStatus("");
    fetchData();
  };

  // SAS token
  useEffect(() => {
    const fetchToken = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/api/v1/soil_test/generate-sas-token?container=soil-test-agrim`
        );
        if (response.data.status === "success") {
          setToken(response.data.sas_token);
        }
      } catch (err) {
        console.log(err);
      }
    };
    fetchToken();
  }, []);

  // Open SHC
  const handleViewSHC = (row) => {
    setSelectedRow(row);
    setOpenShcDialog(true);

    // reset image states each time
    setImageStatus("idle");
    setImageSrc("");
  };

  // Open Report
  const handleViewReport = (row) => {
    setSelectedRow(row);
    setOpenReportDialog(true);
  };

  // Preload sample image for SHC only
  useEffect(() => {
    if (!openShcDialog) return;
    if (!selectedRow || !selectedRow.image_url) return;
    if (!token) return;

    setImageStatus("loading");
    var src = selectedRow.image_url + "?" + token + "&cb=" + new Date().getTime();
    setImageSrc(src);

    var img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = function () {
      setImageStatus("ok");
    };
    img.onerror = function () {
      setImageStatus("fail");
    };
    img.src = src;
  }, [openShcDialog, selectedRow, token]);

  // ====== PDF helpers ======
  const waitForImagesWithTimeout = (container, timeoutMs) => {
    var imgs = container ? Array.from(container.querySelectorAll("img")) : [];
    var promises = imgs.map((img) => {
      if (img.complete) return Promise.resolve();
      return new Promise((resolve) => {
        img.onload = function () { resolve(); };
        img.onerror = function () { resolve(); };
      });
    });

    var timeout = new Promise((resolve) => {
      setTimeout(function () { resolve(); }, timeoutMs);
    });

    return Promise.race([Promise.all(promises), timeout]);
  };

  const exportElementToPdf = async (elementId, filePrefix) => {
    try {
      setPdfLoading(true);

      var input = document.getElementById(elementId);
      if (!input) {
        console.error(elementId + " not found");
        return;
      }

      await waitForImagesWithTimeout(input, 2000);

      var canvas = await html2canvas(input, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#fff",
        scrollX: 0,
        scrollY: -window.scrollY
      });

      var imgData = canvas.toDataURL("image/png");
      var pdf = new jsPDF("p", "mm", "a4");

      var pdfWidth = pdf.internal.pageSize.getWidth();
      var pdfHeight = pdf.internal.pageSize.getHeight();

      var imgWidth = pdfWidth;
      var imgHeight = (canvas.height * pdfWidth) / canvas.width;

      var heightLeft = imgHeight;
      var position = 0;

      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight;

      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pdfHeight;
      }

      var pageCount = pdf.getNumberOfPages();
      for (var i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        pdf.setFontSize(10);
        pdf.text("Page " + i + " of " + pageCount, pdfWidth - 30, pdfHeight - 10);
      }

      var now = new Date();
      var timestamp = now.toLocaleString("en-GB").replace(/[/,:\s]/g, "-");
      var testId =
        selectedRow && selectedRow.id !== undefined && selectedRow.id !== null
          ? selectedRow.id
          : "NA";

      var fileName = filePrefix + "-" + testId + "-" + timestamp + ".pdf";
      pdf.save(fileName);
    } catch (e) {
      console.error("PDF generation failed:", e);
    } finally {
      setPdfLoading(false);
    }
  };

  const handleDownloadSHCPDF = () => exportElementToPdf("pdf-shc", "soil-health-card");
  const handleDownloadReportPDF = () => exportElementToPdf("pdf-report", "soil-test-report");

  // Recommendations (static)
  const staticRecommendations = useMemo(
    () => [
      "Add FYM/compost regularly to improve soil organic matter.",
      "Split nitrogen application (basal + top dressing) to reduce losses.",
      "Use balanced fertilization based on crop requirement.",
      "If pH is acidic/alkaline, consider corrective measures (lime/gypsum) as per agronomist advice."
    ],
    []
  );

  const MetricBox = (props) => {
    return (
      <Box
        sx={{
          border: "1px solid #e0e0e0",
          borderRadius: 2,
          padding: 1.5,
          minWidth: 160
        }}
      >
        <Typography sx={{ fontSize: 12, color: "rgba(0,0,0,0.6)" }}>
          {props.label}
        </Typography>
        <Typography sx={{ fontWeight: 700, fontSize: 16 }}>
          {props.value}
        </Typography>
        {props.chipLabel ? (
          <Box mt={1}>
            <Chip size="small" label={props.chipLabel} color={props.chipColor} />
          </Box>
        ) : null}
      </Box>
    );
  };

  const renderReportValue = (key, value) => {
    if (key === "image_url") {
      if (!value) return "-";
      if (!token) return "Loading image...";
      return (
        <div style={{ width: "14.4375rem", height: "7.8125rem" }}>
          <img
            src={value + "?" + token}
            alt=""
            style={{ width: "100%", height: "100%", objectFit: "contain" }}
          />
        </div>
      );
    }

    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (key.toLowerCase().includes("time")) return formatToIST(value);
    if (value === null || value === undefined || value === "") return "-";
    return String(value);
  };

  // ====== Columns: now includes two buttons ======
  const columns = [
    {
      field: "ids",
      headerName: "S.No",
      renderHeader: () => (
        <strong style={{ color: "rgb(28, 70, 146)" }}>{"SL. No"}</strong>
      ),
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <span style={{ pointerEvents: "none", userSelect: "none" }}>
          {params.api.getAllRowIds().indexOf(params.id) + 1}
        </span>
      )
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 260,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => handleViewSHC(params.row)}
          >
            View SHC
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => handleViewReport(params.row)}
          >
            View Report
          </Button>
        </Box>
      )
    },
    { field: "id", headerName: "Test ID", width: 90 },
    {
      field: "test_start_time",
      headerName: "Test Start Time",
      width: 200,
      valueFormatter: (params) => formatToIST(params.value)
    },
    { field: "soil_type", headerName: "Soil Type", width: 120 },
    { field: "depth_cm", headerName: "Depth (inch)", width: 120 },
    { field: "latitude", headerName: "Latitude", width: 120 },
    { field: "longitude", headerName: "Longitude", width: 120 },
    { field: "test_status", headerName: "Status", width: 130 }
  ];

  const statusOptions = ["running", "started", "complete", "aborted"];

  return (
    <Box sx={{ marginTop: "10vh" }} py={3}>
      <Typography
        sx={{
          color: "rgba(0, 0, 0, 0.80)",
          fontFamily: "Inter, sans-serif",
          fontSize: "1.5rem",
          fontWeight: 600,
          marginBottom: "1.5rem"
        }}
      >
        Recent Soil Tests
      </Typography>

      <Box mb={2} display="flex" gap={4} flexWrap="wrap" alignItems="center">
        <TextField
          label="From Date"
          type="date"
          InputLabelProps={{ shrink: true }}
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
        />
        <TextField
          label="To Date"
          type="date"
          InputLabelProps={{ shrink: true }}
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
        />

        <TextField
          select
          label="Status"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          style={{ minWidth: 150 }}
        >
          <MenuItem value="">All</MenuItem>
          {statusOptions.map((option) => (
            <MenuItem key={option} value={option}>
              {option.replace(/_/g, " ")}
            </MenuItem>
          ))}
        </TextField>

        <Button variant="contained" onClick={handleFilter}>
          Apply
        </Button>
        <Button variant="outlined" onClick={handleReset}>
          Reset
        </Button>
      </Box>

      <DataGrid
        getRowId={(row) => row.id}
        rows={data}
        columns={columns}
        loading={loading}
        density="compact"
        components={{ Toolbar: GridToolbar }}
        pageSize={10}
        rowsPerPageOptions={[10, 25, 50]}
        style={{ maxWidth: "100%", height: "500px" }}
      />

      {/* ================= SHC Dialog ================= */}
      <Dialog
        open={openShcDialog}
        onClose={() => setOpenShcDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ pb: 0 }} />
        <DialogContent>
          <Box
            id="pdf-shc"
            sx={{
              width: "794px",
              margin: "0 auto",
              padding: 3,
              backgroundColor: "#fff",
              fontFamily: "Arial, sans-serif",
              border: "1px solid #ddd",
              borderRadius: 2,
              boxSizing: "border-box"
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <img src="/Agrim.png" alt="AgriM" style={{ height: 45 }} />
              <Box textAlign="center" flexGrow={1}>
                <Typography variant="h5" fontWeight="bold">
                  Soil Health Card
                </Typography>
                <Typography sx={{ fontSize: 12, color: "rgba(0,0,0,0.6)" }}>
                  (Summary for Farmers)
                </Typography>
              </Box>
              <Box sx={{ width: 45 }} />
            </Box>

            <Divider sx={{ mb: 2 }} />

            {selectedRow ? (
              <>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 1,
                    mb: 2
                  }}
                >
                  <Typography sx={{ fontSize: 13 }}>
                    <b>Test ID:</b> {selectedRow.id || "-"}
                  </Typography>
                  <Typography sx={{ fontSize: 13, textAlign: "right" }}>
                    <b>Date:</b> {formatToIST(selectedRow.test_start_time)}
                  </Typography>

                  <Typography sx={{ fontSize: 13 }}>
                    <b>Soil Type:</b> {selectedRow.soil_type || "-"}
                  </Typography>
                  <Typography sx={{ fontSize: 13, textAlign: "right" }}>
                    <b>Depth:</b> {selectedRow.depth_cm || "-"} inch
                  </Typography>

                  <Typography sx={{ fontSize: 13 }}>
                    <b>Lat:</b> {selectedRow.latitude || "-"}
                  </Typography>
                  <Typography sx={{ fontSize: 13, textAlign: "right" }}>
                    <b>Long:</b> {selectedRow.longitude || "-"}
                  </Typography>
                </Box>

                <Typography fontWeight="bold" sx={{ mb: 1 }}>
                  Soil Properties
                </Typography>

                <Box display="flex" gap={1.5} flexWrap="wrap" mb={2}>
                  {(() => {
                    var phBand = getStatusBand(selectedRow.ph, PH_BANDS);
                    var phVal =
                      selectedRow.ph !== undefined && selectedRow.ph !== null
                        ? selectedRow.ph
                        : "-";
                    return (
                      <MetricBox
                        label="pH"
                        value={phVal}
                        chipLabel={phBand.label}
                        chipColor={chipColorFor(phBand.label)}
                      />
                    );
                  })()}

                  <MetricBox
                    label="EC (µs/cm)"
                    value={
                      selectedRow.electrical_conductivity !== undefined &&
                      selectedRow.electrical_conductivity !== null
                        ? selectedRow.electrical_conductivity
                        : "-"
                    }
                  />
                  <MetricBox
                    label="Moisture (%RH)"
                    value={
                      selectedRow.moisture_percent !== undefined &&
                      selectedRow.moisture_percent !== null
                        ? selectedRow.moisture_percent
                        : "-"
                    }
                  />
                  <MetricBox
                    label="Temperature (°C)"
                    value={
                      selectedRow.temperature_c !== undefined &&
                      selectedRow.temperature_c !== null
                        ? selectedRow.temperature_c
                        : "-"
                    }
                  />
                  <MetricBox label="Test Status" value={selectedRow.test_status || "-"} />
                </Box>

                <Divider sx={{ my: 2 }} />

                <Typography fontWeight="bold" sx={{ mb: 1 }}>
                  Nutrient Status (N-P-K)
                </Typography>

                <Box display="flex" gap={1.5} flexWrap="wrap" mb={2}>
                  {(() => {
                    var band = getStatusBand(selectedRow.n, N_BANDS);
                    var val =
                      selectedRow.n !== undefined && selectedRow.n !== null
                        ? selectedRow.n
                        : "-";
                    return (
                      <MetricBox
                        label="Nitrogen (mg/kg)"
                        value={val}
                        chipLabel={band.label}
                        chipColor={chipColorFor(band.label)}
                      />
                    );
                  })()}

                  {(() => {
                    var band = getStatusBand(selectedRow.p, P_BANDS);
                    var val =
                      selectedRow.p !== undefined && selectedRow.p !== null
                        ? selectedRow.p
                        : "-";
                    return (
                      <MetricBox
                        label="Phosphorus (mg/kg)"
                        value={val}
                        chipLabel={band.label}
                        chipColor={chipColorFor(band.label)}
                      />
                    );
                  })()}

                  {(() => {
                    var band = getStatusBand(selectedRow.k, K_BANDS);
                    var val =
                      selectedRow.k !== undefined && selectedRow.k !== null
                        ? selectedRow.k
                        : "-";
                    return (
                      <MetricBox
                        label="Potassium (mg/kg)"
                        value={val}
                        chipLabel={band.label}
                        chipColor={chipColorFor(band.label)}
                      />
                    );
                  })()}
                </Box>

                {selectedRow.image_url ? (
                  imageStatus === "ok" ? (
                    <>
                      <Typography fontWeight="bold" sx={{ mb: 1 }}>
                        Sample Image
                      </Typography>
                      <Box
                        sx={{
                          border: "1px solid #e0e0e0",
                          borderRadius: 2,
                          p: 1,
                          mb: 2
                        }}
                      >
                        <img
                          src={imageSrc}
                          alt=""
                          style={{
                            width: "100%",
                            maxHeight: 220,
                            objectFit: "contain"
                          }}
                        />
                      </Box>
                    </>
                  ) : imageStatus === "loading" ? (
                    <Typography sx={{ fontSize: 12, color: "rgba(0,0,0,0.6)", mb: 2 }}>
                      Loading sample image…
                    </Typography>
                  ) : (
                    <Typography sx={{ fontSize: 12, color: "rgba(0,0,0,0.6)", mb: 2 }}>
                      Sample image not available (could not be loaded).
                    </Typography>
                  )
                ) : null}

                <Divider sx={{ my: 2 }} />

                <Typography fontWeight="bold" sx={{ mb: 1 }}>
                  Recommendations
                </Typography>

                <Box sx={{ pl: 2, mb: 1 }}>
                  {staticRecommendations.map((t, i) => (
                    <Typography key={i} sx={{ fontSize: 13, mb: 0.5 }}>
                      • {t}
                    </Typography>
                  ))}
                </Box>

                <Typography sx={{ fontSize: 13 }}>
                  <b>Remarks:</b>{" "}
                  {selectedRow.remarks !== undefined &&
                  selectedRow.remarks !== null &&
                  selectedRow.remarks !== ""
                    ? String(selectedRow.remarks)
                    : "-"}
                </Typography>
              </>
            ) : null}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleDownloadSHCPDF}
            variant="contained"
            color="success"
            disabled={pdfLoading}
          >
            {pdfLoading ? "Generating PDF..." : "Download SHC PDF"}
          </Button>
          <Button onClick={() => setOpenShcDialog(false)} variant="outlined" disabled={pdfLoading}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* ================= Old Report Dialog ================= */}
      <Dialog
        open={openReportDialog}
        onClose={() => setOpenReportDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ pb: 0 }} />
        <DialogContent>
          <Box
            id="pdf-report"
            sx={{
              padding: 3,
              backgroundColor: "#fff",
              fontFamily: "Arial, sans-serif",
              border: "1px solid #ddd",
              borderRadius: 2
            }}
          >
            <Box display="flex" justifyContent="space-between" mb={3}>
              <img src="/Agrim.png" alt="AgriM" style={{ height: 50 }} />
              <Typography variant="h5" fontWeight="bold" align="center" flexGrow={1}>
                Soil Testing Report
              </Typography>
              <Box sx={{ width: 50 }} />
            </Box>

            {selectedRow
              ? REPORT_SECTIONS.map((section, idx) => (
                  <Box key={idx} sx={{ mb: 2 }}>
                    {section.fields.map((key) => (
                      <Box
                        key={key}
                        display="flex"
                        justifyContent="space-between"
                        mb={1}
                        sx={{
                          borderBottom: "1px dashed #ccc",
                          paddingBottom: "4px"
                        }}
                      >
                        <Typography sx={{ fontWeight: 600 }}>
                          {FIELD_LABELS[key] || key.replace(/_/g, " ")}
                        </Typography>

                        <Typography sx={{ textAlign: "right", maxWidth: "55%" }}>
                          {renderReportValue(key, selectedRow[key])}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                ))
              : null}

            <Typography mt={4} fontWeight="bold" fontSize={16}>
              Lab Test Fields:
            </Typography>
            {LAB_FIELDS.map((label, index) => (
              <Box
                key={index}
                display="flex"
                justifyContent="space-between"
                mb={1}
                sx={{
                  borderBottom: "1px dashed #ccc",
                  paddingBottom: "4px"
                }}
              >
                <Typography sx={{ fontWeight: 600 }}>{label}</Typography>
                <Typography>__________________</Typography>
              </Box>
            ))}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleDownloadReportPDF}
            variant="contained"
            color="success"
            disabled={pdfLoading}
          >
            {pdfLoading ? "Generating PDF..." : "Download Report PDF"}
          </Button>
          <Button onClick={() => setOpenReportDialog(false)} variant="outlined" disabled={pdfLoading}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SoilTestTable;
